import type { RefCallback, RefObject } from "react";

export * from "./clipboard";
export * from "./cn";
export * from "./debounce";
export * from "./getInitials";
export { Slot } from "./Slot";

export const supportsTouchEvents = (): boolean => {
  if (typeof window === "undefined") return false;

  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    ("msMaxTouchPoints" in navigator &&
      (navigator as Navigator & { msMaxTouchPoints: number }).msMaxTouchPoints >
        0)
  );
};

export const isTouchDevice = (): boolean => {
  return (
    supportsTouchEvents() &&
    typeof window !== "undefined" &&
    (navigator.maxTouchPoints > 1 ||
      "ontouchstart" in window ||
      (window.matchMedia && window.matchMedia("(pointer: coarse)").matches))
  );
};

/**
 * Merges multiple refs into a single ref callback
 * Handles both callback refs and mutable ref objects
 * @param refs Array of refs to merge (can include undefined/null)
 * @returns A ref callback that will call all provided refs
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mergeRefs<T = any>(
  ...refs: (RefObject<T | null> | RefCallback<T> | null | undefined)[]
) {
  return (node: T | null) => {
    refs.forEach((ref) => {
      if (ref) {
        if (typeof ref === "function") {
          ref(node);
        } else {
          ref.current = node;
        }
      }
    });
  };
}

export function composeEventHandlers<E>(
  theirs?: (e: E) => void,
  ours?: (e: E) => void,
) {
  return (e: E) => {
    theirs?.(e);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!(e as any).defaultPrevented) {
      ours?.(e);
    }
  };
}
